import type {
  Cause,
  FeedbackOnRemediationType,
  ProductTypeMetadata,
} from "@prisma/client"
import {SkillLevel} from "@prisma/client"
import type {
  DiyStep,
  Part,
  PartType,
  ProductDocument,
  ProductType,
  Video,
} from "@ri2/rc-db"

import {Feedback} from "../feedback"

export type Source =
  | {type: "match"}
  | {type: "brand"; brandName: string; productType: string}
  | {type: "productTypeAndAdditionalDetails"}
  | {type: "productType"}

export type PartsByPartTypeWithSource = {
  source: Source
  partTypes: (PartType & {
    parts: Part[]
  })[]
}

export type DiyVideoWithSource = Video & {source: Source}

export interface DiyStepsWithSource {
  source: Source
  steps: DiyStep[]
}

export type ProductDocumentWithSource = ProductDocument & {source: Source}

export interface RemediationDiyContent {
  videos: DiyVideoWithSource[]
  steps: DiyStepsWithSource | null
  documents: ProductDocumentWithSource[]
}

export const EMPTY_REMEDIATION_DIY_CONTENT: RemediationDiyContent = {
  videos: [],
  steps: null,
  documents: [],
}

export type RemediationWithFeedback = Remediation & {
  feedback: Record<FeedbackOnRemediationType, Feedback | undefined>
}

export type RemediationType =
  | "replaceRemediation"
  | "diyRemediation"
  | "difmRemediation"

/** Canonical RvR card order. Offered sets are always a non-empty ordered subset. */
export const ALL_REMEDIATION_TYPES: RemediationType[] = [
  "diyRemediation",
  "difmRemediation",
  "replaceRemediation",
]

export interface Cost {
  cost: number
  range?: {low: number; high: number}
  source: Source
}

export type Remediation = PriceRanges & {
  cause: Cause & {
    productType: ProductType
    productTypeMetadata: ProductTypeMetadata | null
  }
  recommendation: Recommendation
  partsByPartType: PartsByPartTypeWithSource
  skillLevel: SkillLevel
  estimatedHours: number | null
}

export interface DiyStepsWithSource {
  source: Source
  steps: DiyStep[]
}

export interface MergedDiyRemediation {
  documents: ProductDocumentWithSource[]
  steps: DiyStepsWithSource
  videos: DiyVideoWithSource[]
  skillLevel?: SkillLevel
  estimatedHours?: number
}

export type RecommendationReason =
  | "skillLevel"
  | "cost"
  | "age"
  | "partUnavailable"

export interface Recommendation {
  type: RemediationType
  reasons: RecommendationReason[]
}

export interface PriceRanges {
  diyCost: Cost
  difmCost: Cost
  replaceCost: Cost | null
}
